import React, { useState, useEffect } from 'react';
import {
  Box, Title, Paper, Text, ThemeIcon, Flex, Stack, Group,
  SimpleGrid, Button, RingProgress, Badge, Divider, Progress,
  MultiSelect,
} from '@mantine/core';
import {
  ChartBar, Lightning, Sparkle, CircleNotch,
  Warning, TrendUp, Lightbulb, CurrencyDollar,
  Target, CursorClick, Percent, ShieldCheck, Info,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../hooks/useNotifications';
import SEO from '../components/SEO';
import AnimatedLogo from '../components/AnimatedLogo';
import ExportMenu from '../components/ExportMenu';
import StickyHeader from '../components/StickyHeader';
import api from '../utils/api';
import { formatMoney, currencySymbol } from '../utils/money';

// Dominująca (wg wydatków) waluta zbioru kampanii. Koszty są RAW w walucie konta.
function dominantCurrency(list) {
  const spend = {};
  (list || []).forEach((c) => {
    const cur = c.currency || 'PLN';
    spend[cur] = (spend[cur] || 0) + parseFloat(c.cost || 0);
  });
  const keys = Object.keys(spend);
  if (!keys.length) return 'PLN';
  return keys.reduce((a, b) => (spend[a] >= spend[b] ? a : b));
}

function ScoreRing({ score }) {
  const { t } = useTranslation();
  const color = score >= 80 ? 'green' : score >= 50 ? 'yellow' : 'red';
  const label = score >= 80 ? t('insightsPanel.score.good') : score >= 50 ? t('insightsPanel.score.needsAttention') : t('insightsPanel.score.critical');
  return (
    <Box ta="center">
      <RingProgress
        size={120}
        thickness={10}
        roundCaps
        sections={[{ value: score, color }]}
        label={
          <Box ta="center">
            <Text fz={28} fw={800} ff="monospace" lh={1}>{score}</Text>
            <Text size="xs" c="dimmed">/100</Text>
          </Box>
        }
      />
      <Badge color={color} variant="light" size="sm" mt={4}>{label}</Badge>
    </Box>
  );
}

function StatMini({ icon, label, value, color = 'indigo' }) {
  const Icon = icon;
  return (
    <Paper withBorder p="sm" radius="md">
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon variant="light" color={color} size="md" radius="md">
          <Icon size={16} weight="duotone" />
        </ThemeIcon>
        <Box>
          <Text size="xs" c="dimmed" lh={1.2}>{label}</Text>
          <Text size="sm" fw={600} ff="monospace">{value}</Text>
        </Box>
      </Group>
    </Paper>
  );
}

function IssueCard({ icon, title, color, items }) {
  if (!items || items.length === 0) return null;
  const Icon = icon;
  return (
    <Paper withBorder p="md" radius="md">
      <Group gap="xs" mb="sm">
        <ThemeIcon variant="light" color={color} size="sm" radius="sm">
          <Icon size={14} weight="bold" />
        </ThemeIcon>
        <Text size="sm" fw={600}>{title}</Text>
        <Badge size="xs" variant="light" color={color}>{items.length}</Badge>
      </Group>
      <Stack gap={8}>
        {items.map((item, i) => {
          const text = typeof item === 'string' ? item : item.description || item.title || '';
          return (
            <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
              <Box
                style={{
                  width: 6, height: 6, borderRadius: '50%', marginTop: 7, flexShrink: 0,
                  background: `var(--mantine-color-${color}-5)`,
                }}
              />
              <Text size="sm" lh={1.5} style={{ flex: 1 }}>{text}</Text>
            </Group>
          );
        })}
      </Stack>
    </Paper>
  );
}

export default function Insights() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [audit, setAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  const { addNotification } = useNotifications();

  useEffect(() => {
    Promise.all([
      api.get('/ads/my-campaigns/').catch(() => ({ data: { campaigns: [] } })),
      api.get('/ai/audit/').catch(() => ({ data: null })),
    ]).then(([campRes, auditRes]) => {
      const camps = (campRes.data.campaigns || []).filter(c => c.status !== 'draft');
      setCampaigns(camps);
      if (auditRes.data && auditRes.data.overall_score != null) {
        setAudit(auditRes.data);
      }
    }).finally(() => setLoading(false));
  }, []);

  const runAudit = async () => {
    setAuditing(true);
    try {
      // Kliknięcie przycisku to zawsze świeży audyt (initial load idzie osobnym,
      // cache'owanym GET-em). Bez force backend zwracał cache i przycisk „nie reagował".
      const body = { force: true, ...(selectedCampaignIds.length > 0 ? { campaign_ids: selectedCampaignIds } : {}) };
      const res = await api.post('/ai/audit/', body);
      setAudit(res.data);
    } catch {
      addNotification(t('insightsPanel.errorAudit'), 'error');
    } finally {
      setAuditing(false);
    }
  };

  if (loading) return (
    <Flex align="center" justify="center" h={256}>
      <AnimatedLogo size={48} />
    </Flex>
  );

  if (campaigns.length === 0) {
    return (
      <Box px="lg" py="md">
        <Title order={3} mb="sm">{t('insightsPanel.title')}</Title>
        <Paper withBorder p="xl" radius="md">
          <Stack align="center" gap="xs">
            <ThemeIcon variant="light" color="gray" size="lg" radius="md">
              <ChartBar size={20} />
            </ThemeIcon>
            <Text size="sm" fw={500}>{t('insightsPanel.noCampaigns.title')}</Text>
            <Text size="xs" c="dimmed" ta="center">
              {t('insightsPanel.noCampaigns.text')}
            </Text>
          </Stack>
        </Paper>
      </Box>
    );
  }

  const displayedCampaigns = selectedCampaignIds.length > 0
    ? campaigns.filter(c => selectedCampaignIds.includes(c.id))
    : campaigns;
  const totalSpend = displayedCampaigns.reduce((s, c) => s + parseFloat(c.cost || 0), 0);
  const totalClicks = displayedCampaigns.reduce((s, c) => s + (c.clicks || 0), 0);
  const totalConversions = displayedCampaigns.reduce((s, c) => s + parseFloat(c.conversions || 0), 0);
  const totalImpressions = displayedCampaigns.reduce((s, c) => s + (c.impressions || 0), 0);
  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
  const avgCpc = totalClicks > 0 ? totalSpend / totalClicks : 0;
  const curCode = dominantCurrency(displayedCampaigns);

  return (
    <Box px="lg" py="md">
      <SEO title={t('insightsPanel.seoTitle')} path="/app/insights" noIndex />

      {/* Header */}
      <StickyHeader>
        <Title order={3}>{t('insightsPanel.title')}</Title>
        <Group gap="xs">
          {audit && <ExportMenu baseUrl="/ads/audit/export/" filename="droid-audyt" />}
          <Button
            leftSection={auditing ? <CircleNotch size={14} className="animate-spin" /> : <Sparkle size={14} />}
            onClick={runAudit}
            loading={auditing}
            color="brand"
          >
            {audit ? t('insightsPanel.refresh') : t('insightsPanel.runAi')}
          </Button>
        </Group>
      </StickyHeader>

      {/* Campaign selector */}
      {campaigns.length > 1 && (
        <MultiSelect
          placeholder={t('insightsPanel.selector.placeholder')}
          label={t('insightsPanel.selector.label')}
          data={campaigns.map(c => ({ value: c.id, label: c.name }))}
          value={selectedCampaignIds}
          onChange={setSelectedCampaignIds}
          clearable
          searchable
          size="sm"
          mb="md"
          maxDropdownHeight={200}
          description={selectedCampaignIds.length > 0
            ? t('insightsPanel.selector.selected', { n: selectedCampaignIds.length, total: campaigns.length })
            : t('insightsPanel.selector.hint')
          }
          classNames={{ input: 'insights-multiselect', wrapper: 'insights-multiselect-wrapper' }}
        />
      )}

      {/* Stats overview */}
      <SimpleGrid cols={{ base: 2, md: 5 }} spacing="sm" mb="md">
        <StatMini icon={CurrencyDollar} label={t('insightsPanel.stats.spend30d')} value={formatMoney(totalSpend, curCode)} />
        <StatMini icon={CursorClick} label={t('insightsPanel.stats.clicks')} value={totalClicks.toLocaleString('pl-PL')} color="blue" />
        <StatMini icon={Target} label={t('insightsPanel.stats.conversions')} value={totalConversions.toLocaleString('pl-PL')} color="green" />
        <StatMini icon={Percent} label={t('insightsPanel.stats.avgCtr')} value={`${avgCtr.toFixed(1)}%`} color="yellow" />
        <StatMini icon={CurrencyDollar} label={t('insightsPanel.stats.avgCpc')} value={formatMoney(avgCpc, curCode)} color="gray" />
      </SimpleGrid>

      {audit && audit.overall_score != null ? (
        <Stack gap="md">

          {/* Score + Analysis */}
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Paper withBorder p="lg" radius="md">
              <Group gap="lg" align="center" justify="center" wrap="nowrap" style={{ minHeight: 140 }}>
                <ScoreRing score={audit.overall_score} />
                <Box style={{ flex: 1 }}>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500} mb={4} style={{ letterSpacing: '0.04em' }}>
                    {t('insightsPanel.accountScore')}
                  </Text>
                  {audit.analysis && (
                    <Text size="sm" lh={1.6} c="dark">{audit.analysis}</Text>
                  )}
                </Box>
              </Group>
            </Paper>

            {/* Savings + Campaign breakdown */}
            <Paper withBorder p="lg" radius="md">
              <Text size="xs" c="dimmed" tt="uppercase" fw={500} mb="sm" style={{ letterSpacing: '0.04em' }}>
                {t('insightsPanel.campaignSummary')}
              </Text>
              <Stack gap="xs">
                {displayedCampaigns.slice(0, 6).map((c, i) => {
                  const spend = parseFloat(c.cost || 0);
                  const pct = totalSpend > 0 ? (spend / totalSpend * 100) : 0;
                  return (
                    <Box key={i}>
                      <Group justify="space-between" mb={2}>
                        <Group gap={6} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                          <Box
                            style={{
                              width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                              background: c.status === 'active' ? 'var(--mantine-color-green-5)' : 'var(--mantine-color-gray-4)',
                            }}
                          />
                          <Text size="xs" truncate style={{ flex: 1 }}>{c.name}</Text>
                        </Group>
                        <Text size="xs" c="dimmed" ff="monospace" style={{ flexShrink: 0 }}>{formatMoney(spend, c.currency, { decimals: 0 })} ({pct.toFixed(0)}%)</Text>
                      </Group>
                      <Progress value={pct} size={4} color={c.status === 'active' ? 'brand' : 'gray'} />
                    </Box>
                  );
                })}
              </Stack>
              {audit.estimated_savings > 0 && (
                <>
                  <Divider my="sm" />
                  <Group gap="xs">
                    <ShieldCheck size={16} weight="duotone" className="text-green-600" />
                    <Text size="xs" c="dimmed">{t('insightsPanel.estimatedSavings')}</Text>
                    <Text size="sm" fw={700} ff="monospace" c="green.7">
                      {t('insightsPanel.savingsValue', { value: typeof audit.estimated_savings === 'number' ? audit.estimated_savings.toLocaleString('pl-PL') : audit.estimated_savings, cur: currencySymbol(audit.currency || curCode) })}
                    </Text>
                  </Group>
                </>
              )}
            </Paper>
          </SimpleGrid>

          {/* Issues grid */}
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <IssueCard
              icon={Warning}
              title={t('insightsPanel.issues.critical')}
              color="red"
              items={audit.critical_issues}
            />
            <IssueCard
              icon={Lightning}
              title={t('insightsPanel.issues.waste')}
              color="orange"
              items={audit.waste_areas}
            />
            <IssueCard
              icon={TrendUp}
              title={t('insightsPanel.issues.opportunities')}
              color="green"
              items={audit.opportunities}
            />
            <IssueCard
              icon={Lightbulb}
              title={t('insightsPanel.issues.strategy')}
              color="brand"
              items={audit.strategy_tips}
            />
          </SimpleGrid>

        </Stack>
      ) : audit && audit.summary ? (
        <Paper withBorder p="xl" radius="md" style={{ textAlign: 'center' }}>
          <Info size={24} style={{ display: 'block', margin: '0 auto 8px', color: 'var(--mantine-color-blue-5)' }} />
          <Text size="sm" fw={500} mb={4}>{t('insightsPanel.noResults.title')}</Text>
          <Text size="xs" c="dimmed" mb="md" style={{ maxWidth: 460, marginInline: 'auto' }}>{audit.summary}</Text>
          <Button component={Link} to="/app/campaigns" size="xs" variant="light" color="brand">
            {t('insightsPanel.noResults.toCampaigns')}
          </Button>
        </Paper>
      ) : (
        <Paper withBorder p="xl" radius="md" style={{ textAlign: 'center' }}>
          <Sparkle size={24} style={{ display: 'block', margin: '0 auto 8px', color: 'var(--mantine-color-gray-5)' }} />
          <Text size="sm" fw={500} mb={4}>{t('insightsPanel.prompt.title')}</Text>
          <Text size="xs" c="dimmed">{t('insightsPanel.prompt.text')}</Text>
        </Paper>
      )}
    </Box>
  );
}
